import * as React from 'react';
import '@progress/kendo-theme-bootstrap/dist/all.css';
import StringResourceManager, { GetString } from '../StringResourceManager';
import {
  Dialog,
  DialogActionsBar,
  ActionsLayout,
} from '@progress/kendo-react-dialogs';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { Grid, createStyles, makeStyles } from '@material-ui/core';
import { Button } from '@progress/kendo-react-buttons';
import { Input, InputChangeEvent } from '@progress/kendo-react-inputs';
import {
  EditNBCProfile,
  addNewProfile,
  deleteNBCProfile,
} from '../../store/NBC/NBCAction';
import { NBCProfile } from '../../Interfaces/NBC/NBCItem';
import { ApplicationState } from '../../store';
import ProfileNodeSelection from '../ProfileNodeSelection';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import clsx from 'clsx';
import enableIcon from '../../assets/images/arrow-right.png';
import { Error, Label, Hint } from '@progress/kendo-react-labels';
import _ from 'lodash';

const useStyles = makeStyles(theme =>
  createStyles({
    gridAlign: {
      display: 'flex',
    },
    margin: {
      margin: theme.spacing(1),
    },
    dialogLabel: {
      fontSize: '1.1rem',
      display: 'inline',
    },
    arrowIcon: {
      height: '50px',
      weight: '50px',
    },
    iconGrid: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      margin: 10,
    },
    listBoxGrid: {
      width: '250px',
      marginRight: '10px',
    },
    listBox: {
      border: '1px solid #000000',
    },
    dropdownStyle: {
      marginTop: '25px',
      marginLeft: '25px',
    },
    buttonStyle: {
      alignItems: 'flex-end',
      margin: '25px',
    },
    enableButton: {
      width: '180px',
      border: '1px solid #000000',
    },
    disableButton: {
      width: '180px',
    },
  })
);

interface Props {
  handleCloseDialog: (open: boolean) => void;
}

const ManageProfileDialog: React.FC<Props> = ({
  handleCloseDialog,
}): React.ReactElement => {
  const classes = useStyles();

  const dispatch: Dispatch<any> = useDispatch();

  const profileNames: NBCProfile[] = useSelector(
    (state: ApplicationState) => state.nbcReducer.profileNames
  );

  const [profiles, setProfiles] = React.useState<NBCProfile[]>(profileNames);

  const ERRORMSG = GetString('ERROR_LABEL');

  const PROFILE_NAME = GetString('PROFILE_NAME');

  const CANCEL_BUTTON = GetString('CANCEL_BUTTON');

  const ADD_BUTTON = GetString('ADD_BUTTON');

  const ADD_NEW_PROFILE = GetString('ADD_NEW_PROFILE');

  const YES = GetString('YES');

  const NO = GetString('NO');

  const CONFIRM_MESSAGE = GetString('CONFIRM_MESSAGE');

  const [layout, setLayout] = React.useState<ActionsLayout>('end');

  const [selectedProfile, setSelectedProfile] = React.useState<string>('');

  const [confirmVisible, setConfirmVisible] = React.useState<boolean>(false);

  const [addProfileVisible, setAddProfileVisible] =
    React.useState<boolean>(false);

  const handleClose = () => {
    handleCloseDialog(false);
  };

  const cancelOnClick = () => {
    handleCloseDialog(false);
  };

  const [profile, setProfile] = React.useState<any>();

  const [enabledSaveButton, setEnabledSaveButton] =
    React.useState<boolean>(true);

  const [selectedDivisionNewProfile, setSelectedDivisionNewProfile] =
    React.useState<string[]>([]);

  const [selectedStoreNewProfile, setSelectedStoreNewProfile] = React.useState<
    string[]
  >([]);

  const [selectedNodeGroupNewProfile, setSelectedNodeGroupNewProfile] =
    React.useState<string[]>([]);

  const [selectedLaneNewProfile, setSelectedLaneNewProfile] = React.useState<
    string[]
  >([]);

  const [confirmDelete, setIsConfirmDelete] = React.useState<boolean>(false);

  const [deleteProfiles, setDeleteProfiles] = React.useState<
    string | undefined
  >();

  const profileNameOnChange = (e: InputChangeEvent) => {
    const newProfileName = e.target.value;
    setProfile(newProfileName);

    const isExistingProfile = profileNames.some(
      a => a.ProfileName === newProfileName
    );
    const isSameAsSelectedProfile = newProfileName === selectedProfile;

    setIsProfileNameExist(isExistingProfile && !isSameAsSelectedProfile);
    setEnabledSaveButton(isExistingProfile || isSameAsSelectedProfile);
  };

  let selectedNBCProfile = profileNames.find(
    (a: NBCProfile) => a.ProfileName === selectedProfile
  );

  const [selectedDivisions, setSelectedDivisions] = React.useState<
    string[] | undefined
  >(undefined);

  const [selectedStores, setSelectedStores] = React.useState<
    string[] | undefined
  >(undefined);

  const [selectedNodeGroup, setSelectedNodeGroup] = React.useState<
    string[] | undefined
  >(undefined);

  const [selectedLanes, setSelectedLanes] = React.useState<
    string[] | undefined
  >(undefined);

  const saveOnClick = () => {
    let newNBCProfile: NBCProfile = {
      ProfileId: selectedNBCProfile?.ProfileId,
      ProfileName: profile ? profile : selectedProfile,
      ProfileUuid: selectedNBCProfile?.ProfileUuid,
      Divisions:
        selectedDivisions === undefined
          ? selectedNBCProfile?.Divisions
          : (selectedDivisions ?? []).length === 0
          ? []
          : selectedDivisions,
      SiteNumbers:
        selectedStores === undefined
          ? selectedNBCProfile?.SiteNumbers
          : (selectedStores ?? []).length === 0
          ? []
          : selectedStores,
      NodeGroups:
        selectedNodeGroup === undefined
          ? selectedNBCProfile?.NodeGroups
          : (selectedNodeGroup ?? []).length === 0
          ? []
          : selectedNodeGroup,
      LaneNumbers:
        selectedLanes === undefined
          ? selectedNBCProfile?.LaneNumbers
          : selectedLanes,
    };

    EditNBCProfile(dispatch, newNBCProfile);

    handleClose();
  };

  React.useEffect(() => {
    if (confirmDelete) {
      deleteNBCProfile(
        dispatch,
        profileNames.find((a: NBCProfile) => a.ProfileUuid === deleteProfiles)
      );
      setIsConfirmDelete(false);
    }
  }, [confirmDelete]);

  const nbcItemProfileInfo = profileNames.find(
    (a: NBCProfile) => a.ProfileName === selectedProfile
  );

  React.useEffect(() => {
    let newNBCProfileToCompare: NBCProfile = {
      ProfileName: selectedProfile,
      ProfileId: nbcItemProfileInfo?.ProfileId,
      ProfileUuid: nbcItemProfileInfo?.ProfileUuid,
      Items: null,
      Divisions: selectedDivisions ?? [],
      SiteNumbers: selectedStores ?? [],
      NodeGroups: selectedNodeGroup ?? [],
      LaneNumbers: selectedLanes ?? [],
    };

    if (newNBCProfileToCompare.ProfileId) {
      if (_.isEqual(newNBCProfileToCompare, selectedNBCProfile)) {
        setEnabledSaveButton(true);
      } else {
        setEnabledSaveButton(false);
      }
    }
  }, [
    selectedDivisions,
    selectedStores,
    selectedNodeGroup,
    selectedLanes,
    selectedProfile,
  ]);

  const handleConfirm = () => {
    const updatedProfiles = profiles.filter(
      profile => profile.ProfileName !== selectedProfile
    );
    setProfiles(updatedProfiles);

    setIsConfirmDelete(true);
    setConfirmVisible(false);
  };

  const handleCancelConfirm = () => {
    setConfirmVisible(false);
  };

  const setDivisionProfile = (division: string[]) => {
    if (division.length === 0) {
      setSelectedDivisions([]);
      setSelectedStores([]);
      setSelectedNodeGroup([]);
      setSelectedLanes([]);
    } else if (division.length > 1) {
      setSelectedDivisions(division);
      setSelectedStores([]);
      setSelectedNodeGroup([]);
      setSelectedLanes([]);
    } else {
      setSelectedDivisions(division);
    }
  };

  const setStoreProfile = (stores: string[]) => {
    if (stores.length === 0) {
      setSelectedNodeGroup([]);
      setSelectedLanes([]);
      setSelectedStores([]);
    } else if (stores.length > 1) {
      setSelectedNodeGroup([]);
      setSelectedLanes([]);
      setSelectedStores(stores);
    } else {
      setSelectedStores(stores);
    }
  };

  const setNodeGroupProfile = (nodeGroups: string[]) => {
    if (nodeGroups.length === 0) {
      setSelectedLanes([]);
      setSelectedNodeGroup([]);
    } else if (nodeGroups.length > 1) {
      setSelectedLanes([]);
      setSelectedNodeGroup(nodeGroups);
    } else {
      setSelectedNodeGroup(nodeGroups);
    }
  };

  const setLanesGroupProfile = (lanes: string[]) => {
    setSelectedLanes(lanes);
  };

  const handleEdit = (event: NBCProfile) => () => {
    if (selectedProfile !== event.ProfileName) {
      setSelectedDivisions(event.Divisions);
      setSelectedStores(event.SiteNumbers);
      setSelectedNodeGroup(event.NodeGroups);
      setSelectedLanes(event.LaneNumbers);
      setSelectedProfile(event.ProfileName);
    }
  };

  const handleDelete = (event: NBCProfile) => () => {
    setConfirmVisible(true);
    setDeleteProfiles(event.ProfileUuid);
    setSelectedProfile('');
  };

  const [newProfile, setNewProfile] = React.useState<any>();

  const [isProfileExist, setIsProfileExist] = React.useState<boolean>(false);

  const [isProfileNameExist, setIsProfileNameExist] =
    React.useState<boolean>(false);

  const [enableAddProfileButton, setEnableAddProfileButton] =
    React.useState<boolean>(true);

  const inputOnChange = (e: InputChangeEvent) => {
    const value = e.target.value;
    setNewProfile(value);

    const profileExists = profileNames.some(
      profile => profile.ProfileName === value
    );

    setIsProfileExist(profileExists);
    setEnableAddProfileButton(value === '' || profileExists);
  };

  const setDivisionNewProfile = (divisions: string[]) => {
    setSelectedDivisionNewProfile(divisions);
  };

  const setStoreNewProfile = (stores: string[]) => {
    setSelectedStoreNewProfile(stores);
  };

  const setNodeGroupNewProfile = (nodeGroups: string[]) => {
    setSelectedNodeGroupNewProfile(nodeGroups);
  };

  const setLanesNewProfile = (lanes: string[]) => {
    setSelectedLaneNewProfile(lanes);
  };

  const handleSaveNewProfile = () => {
    let newNBCProfile: NBCProfile = {
      ProfileName: newProfile,
      Divisions: selectedDivisionNewProfile,
      SiteNumbers: selectedStoreNewProfile,
      NodeGroups: selectedNodeGroupNewProfile,
      LaneNumbers: selectedLaneNewProfile,
    };
    addNewProfile(dispatch, newNBCProfile);
    setNewProfile('');

    setSelectedStoreNewProfile([]);

    setSelectedNodeGroupNewProfile([]);

    setSelectedLaneNewProfile([]);

    setEnableAddProfileButton(true);

    setAddProfileVisible(false);

    setAddProfileVisible(false);
  };

  return (
    <>
      <Dialog
        style={{ zIndex: 10000 }}
        height="75vh"
        minWidth="55vw"
        title={
          <StringResourceManager
            variant="body1"
            style="commandLabel"
            stringKey={'MANAGE_PROFILES'}
            gutterBottom={true}
          />
        }
        onClose={handleClose}
      >
        <Grid className={clsx(classes.gridAlign, classes.margin)}>
          <Grid className={classes.listBoxGrid}>
            <Grid>
              <StringResourceManager
                variant="body1"
                style="dialogLabel"
                stringKey={'SELECT_PROFILE'}
                gutterBottom={true}
              />
            </Grid>
            <Grid className={classes.listBox}>
              <List
                sx={{
                  width: '100%',
                  height: 300,
                  maxWidth: 360,
                  bgcolor: 'background.paper',
                  overflow: 'auto',
                }}
              >
                {profileNames.map(nbcProfile => (
                  <ListItem key={nbcProfile.ProfileUuid}>
                    <div>
                      <div style={{ display: 'flex', flexDirection: 'row' }}>
                        <div>
                          <Input
                            defaultValue={nbcProfile.ProfileName}
                            disabled={
                              nbcProfile.ProfileName !== selectedProfile
                            }
                            onChange={profileNameOnChange}
                            onClick={handleEdit(nbcProfile)}
                          />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'row' }}>
                          <IconButton onClick={handleEdit(nbcProfile)}>
                            <EditIcon />
                          </IconButton>
                          <IconButton onClick={handleDelete(nbcProfile)}>
                            <DeleteIcon />
                          </IconButton>
                        </div>
                      </div>
                      <div hidden={nbcProfile.ProfileName !== selectedProfile}>
                        {isProfileNameExist && (
                          <Error direction={'start'}>{ERRORMSG}</Error>
                        )}
                      </div>
                    </div>
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid className={clsx(classes.gridAlign, classes.margin)}>
              <Button
                className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
                onClick={() => setAddProfileVisible(true)}
                style={{ marginLeft: '40px' }}
              >
                {ADD_NEW_PROFILE}
              </Button>
            </Grid>
          </Grid>
          <Grid
            className={classes.iconGrid}
            hidden={selectedProfile !== '' ? false : true}
          >
            <img src={enableIcon} className={classes.arrowIcon} />
          </Grid>
          {selectedProfile && (
            <ProfileNodeSelection
              selectedProfile={selectedProfile}
              setDivisionProfile={setDivisionProfile}
              setStoreProfile={setStoreProfile}
              setNodeGroupProfile={setNodeGroupProfile}
              setLanesProfile={setLanesGroupProfile}
            />
          )}
        </Grid>

        <DialogActionsBar layout={layout}>
          <Button
            className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
            style={{ width: 100 }}
            onClick={cancelOnClick}
          >
            {GetString('CANCEL_BUTTON')}
          </Button>
          <Button
            className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
            style={{ width: 100 }}
            onClick={saveOnClick}
            disabled={enabledSaveButton}
          >
            {GetString('SAVE_BUTTON')}
          </Button>
        </DialogActionsBar>
      </Dialog>

      {addProfileVisible && (
        <Dialog
          height="75vh"
          minWidth="55vw"
          title={
            <StringResourceManager
              variant="body1"
              style="commandLabel"
              stringKey={'ADD_NEW_PROFILE'}
              gutterBottom={true}
            />
          }
          onClose={() => setAddProfileVisible(false)}
        >
          <Grid>
            <div
              className={'k-form-field-wrap'}
              style={{ marginBottom: '10px' }}
            >
              <Label>{PROFILE_NAME}</Label>
              <br />
              <Input
                onChange={inputOnChange}
                value={newProfile}
                style={{
                  borderColor: isProfileExist ? 'red' : 'black',
                  width: '260px',
                }}
              />
              {isProfileExist && <Error direction={'start'}>{ERRORMSG}</Error>}
            </div>
            <Grid className={clsx(classes.gridAlign, classes.margin)}>
              {newProfile && !isProfileExist && (
                <ProfileNodeSelection
                  setDivisionProfile={setDivisionNewProfile}
                  setStoreProfile={setStoreNewProfile}
                  setNodeGroupProfile={setNodeGroupNewProfile}
                  setLanesProfile={setLanesNewProfile}
                />
              )}
            </Grid>
          </Grid>
          <DialogActionsBar layout={layout}>
            <Button
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
              style={{ width: 100 }}
              onClick={() => setAddProfileVisible(false)}
            >
              {CANCEL_BUTTON}
            </Button>
            <Button
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
              style={{ width: 100 }}
              onClick={handleSaveNewProfile}
              disabled={enableAddProfileButton}
            >
              {ADD_BUTTON}
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}

      {confirmVisible && (
        <Dialog
          style={{ zIndex: 10001 }}
          title="Confirmation"
          onClose={handleCancelConfirm}
        >
          <p>{CONFIRM_MESSAGE}</p>
          <DialogActionsBar>
            <Button onClick={handleConfirm}>{YES}</Button>
            <Button onClick={handleCancelConfirm}>{NO}</Button>
          </DialogActionsBar>
        </Dialog>
      )}
    </>
  );
};

export default ManageProfileDialog;
